import { useState } from 'react';
import { Container, Typography, Box, Button, Alert, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { importExpenses } from '../api/importApi';

export default function ImportExpenses() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onPreview = async () => {
    try {
      if (!file) return;
      const res = await importExpenses(file, true);
      setResult(res);
      setError(null);
    } catch (e: any) { setError(e.message || 'Erreur lors de la prévisualisation'); }
  };

  const onConfirm = async () => {
    try {
      if (!file) return;
      const res = await importExpenses(file, false);
      setResult(res);
      setError(null);
    } catch (e: any) { setError(e.message || "Erreur lors de l'import"); }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography variant="h4" gutterBottom>Import Excel — Dépenses</Typography>
      <Typography color="text.secondary" gutterBottom>Chargez un fichier Excel, vérifiez la prévisualisation (50 premières lignes) puis confirmez l'import.</Typography>

      <Box my={2} display="flex" gap={2} alignItems="center">
        <input type="file" accept=".xlsx,.xls,.ods,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <Button variant="outlined" onClick={onPreview} disabled={!file}>Prévisualiser</Button>
        <Button variant="contained" onClick={onConfirm} disabled={!file}>Confirmer l'import</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {result && (
        <Box>
          <Typography variant="h6" gutterBottom>Rapport</Typography>
          <pre style={{ background: '#f6f6f6', padding: 12, borderRadius: 6, maxHeight: 220, overflow: 'auto' }}>{JSON.stringify(result.summary || result, null, 2)}</pre>

          {result.unresolvedEntities && (
            <Box my={2}>
              <Typography variant="h6">Éléments non résolus</Typography>
              <Box display="flex" gap={2} my={1} flexWrap="wrap">
                <Box>
                  <Typography variant="subtitle2">Chauffeurs</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">{(result.unresolvedEntities.drivers || []).map((d: string) => <Chip key={d} label={d} />)}</Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Camions</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">{(result.unresolvedEntities.trucks || []).map((d: string) => <Chip key={d} label={d} />)}</Box>
                </Box>
              </Box>
            </Box>
          )}

          {result.preview && (
            <Box mt={3}>
              <Typography variant="h6">Prévisualisation (50 premières lignes)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Chauffeur</TableCell>
                    <TableCell>Camion</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Montant</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.preview.map((r: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{r.date ? new Date(r.date).toLocaleDateString() : ''}</TableCell>
                      <TableCell>{r.driverId || r.driverName || ''}</TableCell>
                      <TableCell>{r.truckId || r.truckName || ''}</TableCell>
                      <TableCell>{r.type} / {r.entryType}</TableCell>
                      <TableCell>{r.amount ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
}
